/*
 * The Foozie cart: the drawer's and the /cart page's behaviour (session A4).
 *
 * The server renders every state (snippets/fz-cart-body.liquid). This file
 * makes the cart feel instant and does the few things only a browser can:
 *
 * - OPTIMISTIC UPDATES. MonsterCart dimmed the whole drawer behind a spinner
 *   on every tap. Here a stepper, a remove, the one-tap add, the upsell and
 *   Covered Shipping move the bar, the headline, the bundle slot and the totals
 *   the moment they are pressed, predicted by assets/fz-cart-tiers.js; the
 *   server's render (Horizon's <cart-items-component> morphs it in) then
 *   lands on top and wins. A failed request repaints what the server last said.
 * - THE DRAWER'S OWN ADDS go through Horizon's CartLinesUpdateEvent, so the
 *   drawer re-renders the way it does for a PDP add, the header count and
 *   session A5a's bundle counter hear about them, and Shopify's standard
 *   add-to-cart event (every web pixel's) fires as it does for any add.
 *   Nothing here ever writes cart ATTRIBUTES, so the Rollouts arm tag's
 *   _theme_arm_first / _theme_arm_last survive every step (plan section 3.7).
 * - THE UNLOCK: when a tier turns on, its milestone pops once, and the drawer
 *   throws a short confetti (Luke, L-100): once per goal per cart, never
 *   replayed on a later open, and not at all under reduced motion.
 * - THE TAB NUDGE: parity with the Tab Recovery app, and asleep while that
 *   app is installed (see tabNudge below).
 */

import { ThemeEvents } from "@theme/events";
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from "@shopify/events";

const tiersLink = /** @type {HTMLLinkElement | null} */ (document.querySelector("link[data-fz-cart-tiers]"));
/** @type {Promise<typeof import('./fz-cart-tiers.js')>} */
const tiersReady = import(tiersLink?.href ?? new URL("fz-cart-tiers.js", import.meta.url).href);

const HEADLINE_TEMPLATE = {
  shipping_remaining: "tShippingRemaining",
  shipping_unlocked: "tShippingUnlocked",
  all_unlocked: "tAllUnlocked",
  bundle_unlocked: "tBundleUnlocked",
};

/**
 * The bar's line when the pair tier is next. At 3 pairs I-39's slot under it
 * already says "Add one more pair. It's free." with its one-tap ADD ANOTHER, so
 * the bar only counts (Luke, L-87: the offer is said once). With nothing to add
 * the slot is the banner and the bar keeps its whole line. fz-cart-body.liquid
 * makes the same choice.
 */
const pairKey = (state) =>
  state.bar.pairsNeeded !== 1 ? "tPairMany" : state.slot === "one_more" ? "tPairOneShort" : "tPairOne";

/** The one bar as TEXT, for explain(): marker order, then the fill (4 places). */
const barSignature = (order, fill) => `${order.join(">")}@${Math.round(Number(fill || 0) * 10000) / 10000}`;

const reducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const escapeHtml = (s) =>
  String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);

/*
 * THE CONFETTI (Luke, L-100, 2026-10-08: "yes per your reccs"; the
 * recommendation: "inside the drawer only, brand colours, once per goal per
 * cart (no replays), and off for shoppers who prefer reduced motion"). Live's
 * MonsterCart throws a 2.1 s canvas confetti across the drawer at both goals
 * and replays it on most later opens (cart parity run, 2026-10-08).
 *
 * Calmer here: 28 pieces leave the marker of the goal just reached, rise, and
 * fall under gravity, in the brand's purples and corals, 1.3 to 1.8 s each,
 * inside the panel and clipped to it. Transform and opacity only (WAAPI, on
 * the compositor), pointer-events none so it never takes a tap, aria-hidden,
 * gone the moment the drawer closes. A goal is "celebrated" once per cart: the
 * set lives in sessionStorage so a later page cannot replay it, and an emptied
 * cart starts a new one. An unlock the shopper could not see (the drawer was
 * shut, as on the bundle page) is shown once, on the next open.
 */
const CONFETTI_COLOURS = ["#9d4edd", "#b185db", "#e94f37", "#ff6b35"];
const CONFETTI_PIECES = 28;
const CELEBRATED_KEY = "fz-cart-celebrated";
// The rise decelerates (the strong ease-out); the fall accelerates, because
// that is what falling is. This is physics, not a UI response, so it is the one
// place an accelerating curve belongs.
const CONFETTI_RISE = "cubic-bezier(0.23, 1, 0.32, 1)";
const CONFETTI_FALL = "cubic-bezier(0.45, 0, 0.75, 0.6)";
const readCelebrated = () => {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(CELEBRATED_KEY) || "[]"));
  } catch {
    return new Set();
  }
};
const writeCelebrated = (/** @type {Set<string>} */ set) => {
  try {
    sessionStorage.setItem(CELEBRATED_KEY, JSON.stringify([...set]));
  } catch {
    /* private mode: once per page instead of once per cart */
  }
};

/** Set an attribute only when it differs, so a MutationObserver's own writes settle. */
const setAttr = (/** @type {Element} */ el, /** @type {string} */ name, /** @type {string} */ value) => {
  if (el.getAttribute(name) !== value) el.setAttribute(name, value);
};

const sectionIds = () =>
  [...document.querySelectorAll("cart-items-component[data-section-id]")]
    .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
    .filter(Boolean);

class FzCart extends HTMLElement {
  static observedAttributes = ["data-state"];

  /** @type {any} */ #server = null;
  /** @type {any[]} */ #lines = [];
  #otherDiscount = 0;
  /** @type {{ shipping: boolean, bundle: boolean } | null} */ #unlocked = null;
  /** @type {any} */ #tiers = null;
  /** @type {(cents: number, opts?: { trim?: boolean, exact?: boolean }) => string} */ #money = (c) => String(c / 100);
  #celebrateTimer = 0;
  /** An add from the page (the PDP, a grid card) that is still waiting for Shopify. */
  /** @type {{ variantId: string } | null} */ #pageAdd = null;
  /** The page's refusal the drawer is saying on a line, while it covers the page. */
  /** @type {{ variantId: string, message: string } | null} */ #refusal = null;
  /** @type {HTMLDialogElement | null} */ #dialog = null;
  /** Watches Horizon's re-renders, which write the server's markup over ours. */
  /** @type {MutationObserver | null} */ #observer = null;
  #afterRenderFrame = 0;
  /** Goals this cart has celebrated (L-100), and one the shopper has yet to see. */
  #celebrated = readCelebrated();
  /** @type {string | null} */ #pendingConfetti = null;
  /** @type {HTMLElement | null} */ #confettiLayer = null;
  #confettiTimer = 0;

  constructor() {
    super();
    // On the element itself, once: added in connectedCallback they would
    // double up if the element were ever re-attached, and an add would fire twice.
    this.addEventListener("click", this.#onClick);
    this.addEventListener("change", this.#onChange);
    this.addEventListener("keydown", this.#onKeyDown);
  }

  connectedCallback() {
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantity);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onLinesUpdate);
    document.addEventListener(StandardEvents.cartError, this.#onCartError);
    this.#dialog = this.closest("dialog");
    this.#dialog?.addEventListener("close", this.#onDrawerClose);
    document.addEventListener("theme-drawer:open", this.#onDrawerOpen);
    document.addEventListener("theme-drawer:close", this.#onDrawerClosing);
    // AFTER A RE-RENDER, PUT BACK WHAT ONLY THE BROWSER KNOWS (cart parity run,
    // 2026-10-08). Horizon re-renders the drawer after every cart request, and
    // after a REFUSED add from the page it does so with no change to the cart
    // (product-form resolves with no sections, so cart-items-component asks
    // for the section and morphs it in). That morph wrote the server's hidden
    // error box over the painted refusal, and because the cart was unchanged
    // data-state was too, so nothing repainted: the drawer opened unchanged and
    // the PDP's copy stayed hidden behind the flag. The stepper's names are the
    // same kind of state. Watching the subtree is the one signal every render
    // path gives; the work is idempotent, so its own writes settle in a frame.
    this.#observer = new MutationObserver(() => this.#scheduleAfterRender());
    this.#observer.observe(this, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["class", "hidden", "value", "max", "disabled", "aria-label", "aria-describedby"],
    });
    this.#scheduleAfterRender();
    tiersReady.then((tiers) => {
      this.#tiers = tiers;
      this.#read({ celebrate: false });
    });
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantity);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onLinesUpdate);
    document.removeEventListener(StandardEvents.cartError, this.#onCartError);
    this.#dialog?.removeEventListener("close", this.#onDrawerClose);
    document.removeEventListener("theme-drawer:open", this.#onDrawerOpen);
    document.removeEventListener("theme-drawer:close", this.#onDrawerClosing);
    this.#stopConfetti();
    this.#dialog = null;
    this.#observer?.disconnect();
    this.#observer = null;
    cancelAnimationFrame(this.#afterRenderFrame);
    clearTimeout(this.#celebrateTimer);
  }

  #scheduleAfterRender() {
    if (this.#afterRenderFrame) return;
    this.#afterRenderFrame = requestAnimationFrame(() => {
      this.#afterRenderFrame = 0;
      this.#paintRefusal();
      this.#syncSteppers();
    });
  }

  /**
   * THE STEPPER SAYS WHAT ITS LIMITS DO (cart parity run, 2026-10-08).
   *
   * - At the stock, + is greyed (the line's max) and the line says why: the
   *   reason fz-cart-line.liquid renders, shown here the moment a + reaches the
   *   stock rather than when the server answers, and named as the button's
   *   description. Live's MonsterCart let + be pressed and then said "Only 1
   *   item was added to your cart due to availability".
   * - At 1, - is the line's remove, as it is on live: it is pressable (the line
   *   passes min 0), it is named as the line's bin is, and the press goes to
   *   the same remove (see the window listener at the end of this file).
   *
   * Only attributes this code set are taken off again, so Horizon's own
   * markup is never edited away.
   */
  #syncSteppers() {
    for (const row of /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll(".fz-line"))) {
      const input = /** @type {HTMLInputElement | null} */ (row.querySelector("input[data-cart-line]"));
      if (!input) continue;
      const value = Number(input.value);
      const max = input.max === "" ? null : Number(input.max);
      const minus = row.querySelector(".quantity-minus");
      const plus = row.querySelector(".quantity-plus");
      const bin = /** @type {HTMLElement | null} */ (row.querySelector(".fz-line__remove"));
      const removes = value <= 1 && !!bin && !bin.hidden && !!bin.getAttribute("aria-label");
      if (minus) {
        if (removes) {
          setAttr(minus, "aria-label", bin.getAttribute("aria-label"));
          setAttr(minus, "data-fz-removes", "");
        } else if (minus.hasAttribute("data-fz-removes")) {
          minus.removeAttribute("aria-label");
          minus.removeAttribute("data-fz-removes");
        }
      }
      const cap = /** @type {HTMLElement | null} */ (row.querySelector("[data-fz-cap]"));
      if (!cap) continue;
      const atCap = max !== null && value >= max;
      if (cap.hidden === atCap) cap.hidden = !atCap;
      if (plus && atCap && cap.id) setAttr(plus, "aria-describedby", cap.id);
      else if (plus?.getAttribute("aria-describedby") === cap.id) plus.removeAttribute("aria-describedby");
    }
  }

  /** The server's render arrived (Horizon's morph copies the new data-state). */
  attributeChangedCallback() {
    // A partial add creates its line only in this render, and the morph sets
    // this attribute before it reaches the rows: paint after it has finished.
    if (this.#refusal) requestAnimationFrame(() => this.#paintRefusal());
    if (!this.#tiers) return;
    this.removeAttribute("data-pending");
    this.#read({ celebrate: true });
  }

  /** Take the server's word: its lines, its totals, its unlocked tiers. */
  #read({ celebrate }) {
    const raw = this.dataset.state;
    if (!raw) {
      this.#server = null;
      this.#lines = [];
      return;
    }
    try {
      this.#server = JSON.parse(raw);
    } catch {
      this.#server = null;
      return;
    }
    const tiers = this.#tiers;
    // An emptied cart is a new cart: its goals may be celebrated again.
    if (this.#server.itemCount === 0 && (this.#celebrated.size || this.#pendingConfetti)) {
      this.#celebrated.clear();
      this.#pendingConfetti = null;
      writeCelebrated(this.#celebrated);
    }
    this.#lines = this.#server.lines;
    this.#money = tiers.moneyFormatter(this.#server.probe);
    this.#otherDiscount = tiers.otherDiscountCents(this.#lines, this.#server.totalCents);
    const state = this.#stateFor(tiers.serverTotals(this.#lines, this.#server.cartDiscountCents || 0));
    this.#noteUnlocks(state, celebrate);
  }

  #stateFor(totals) {
    const s = this.#server;
    return this.#tiers.tierState(totals, {
      thresholdCents: s.thresholdCents,
      market: s.market,
      nextPairCents: s.next ? s.next.unitCents : null,
      leadPairCents: s.leadCents === undefined ? (s.next ? s.next.unitCents : null) : s.leadCents,
    });
  }

  /**
   * The 2-bundle offer for a set of lines (bundleOfferState in the tiers).
   * Its discount is inside #otherDiscount (it is not Buy 3 Get 4th), so a
   * prediction swaps the server's amount for the predicted one.
   */
  #offerFor(lines) {
    return this.#tiers.bundleOfferState(lines, this.#server?.bundleOffer);
  }

  /** Predict from the current lines and paint it. */
  #predict() {
    if (!this.#server || !this.#tiers) return;
    const offer = this.#offerFor(this.#lines);
    const serverOffer = this.#server.bundleOffer?.discountCents || 0;
    const other = Math.max(0, this.#otherDiscount - serverOffer + offer.discountCents);
    const totals = this.#tiers.predictTotals(this.#lines, other);
    const state = this.#stateFor(totals);
    this.toggleAttribute("data-pending", true);
    this.#paint(state, totals, offer);
    this.#noteUnlocks(state, true);
  }

  /** A request failed: repaint exactly what the server last rendered. */
  #revert() {
    if (!this.#server || !this.#tiers) return;
    this.removeAttribute("data-pending");
    this.#lines = this.#server.lines;
    const totals = this.#tiers.serverTotals(this.#lines, this.#server.cartDiscountCents || 0);
    const state = this.#stateFor(totals);
    this.#paint(state, totals, this.#offerFor(this.#lines));
    this.#noteUnlocks(state, false);
  }

  /**
   * The texts the rules produce for a state. Used to paint, and by
   * bin/fz-cart-journey.mjs to check the rules against Liquid's render.
   */
  #texts(state, totals, bundleOffer) {
    const goal = /** @type {HTMLElement | null} */ (this.querySelector(".fz-goal"));
    const offerRow = /** @type {HTMLElement | null} */ (this.querySelector("[data-fz-offer-row]"));
    const offer = /** @type {HTMLElement | null} */ (this.querySelector('[data-slot-panel="unlocked"]'));
    const install = /** @type {HTMLElement | null} */ (this.querySelector("[data-fz-installment-text]"));
    const money = (c) => escapeHtml(this.#money(c, { trim: true }));
    // A division keeps its cents even where the format has none (fz-cart-money-exact).
    const exact = (c) => escapeHtml(this.#money(c, { trim: true, exact: true }));
    const key = HEADLINE_TEMPLATE[state.headline];
    const fill = this.#tiers.fill;
    let perPair = "";
    if (offer && state.perPairCents != null) {
      const template = state.bundle.free > 1 ? offer.dataset.tMany : offer.dataset.tOne;
      perPair = fill(template ?? "", {
        pairs: state.pairs,
        free: state.bundle.free,
        total: money(state.pairsCents),
        per_pair: exact(state.perPairCents),
      });
    }
    return {
      headline: !goal
        ? ""
        : state.headline === "pair_remaining"
          ? fill(goal.dataset[pairKey(state)] ?? "", { pairs: state.bar.pairsNeeded })
          : key
            ? fill(goal.dataset[key] ?? "", { amount: money(state.shipping.remainingCents) })
            : "",
      bar: barSignature(state.bar.order, state.bar.fill),
      slot: state.slot,
      perPair,
      savings: totals.savingsCents > 0 ? `-${money(totals.savingsCents)}` : "",
      subtotal: money(totals.totalCents),
      installment: install?.dataset.t
        ? fill(install.dataset.t, { amount: exact(Math.floor(totals.totalCents / 4)) })
        : "",
      // The applied offer by the discount's OWN title once Shopify has given
      // one; before that, the same sentence from the server's data-t-title.
      offerTitle:
        bundleOffer?.discountCents > 0 ? this.#server?.bundleOffer?.title || offerRow?.dataset.tTitle || "" : "",
      offerAmount: bundleOffer?.discountCents > 0 ? `-${money(bundleOffer.discountCents)}` : "",
      nudge: bundleOffer?.nudge ? "1" : "",
    };
  }

  /**
   * What the rules say about the server's own cart, beside what Liquid
   * rendered for it. Read-only; bin/fz-cart-journey.mjs asserts the two are
   * equal after every step, which is what keeps the JS twin and the Liquid
   * rules from drifting apart.
   */
  explain() {
    if (!this.#server || !this.#tiers) return null;
    // Compared as TEXT: the server escapes an apostrophe in a translation
    // (&#39;) that the browser reads back unescaped, and both show "That's".
    const scratch = document.createElement("div");
    const norm = (html) => {
      scratch.innerHTML = String(html ?? "");
      return (scratch.textContent ?? "").replace(/\s+/g, " ").trim();
    };
    const server = this.#tiers.serverTotals(this.#server.lines, this.#server.cartDiscountCents || 0);
    const predicted = this.#tiers.predictTotals(this.#server.lines, this.#otherDiscount);
    const state = this.#stateFor(server);
    const rules = this.#texts(state, server, this.#offerFor(this.#server.lines));
    const text = (sel) => norm(this.querySelector(sel)?.innerHTML);
    const savingsRow = /** @type {HTMLElement | null} */ (this.querySelector(".fz-sum--savings"));
    const rendered = {
      headline: text("[data-fz-headline]"),
      slot: /** @type {HTMLElement | null} */ (this.querySelector(".fz-offer"))?.dataset.slot ?? "",
      perPair: text("[data-fz-per-pair]"),
      savings: savingsRow && !savingsRow.hidden ? norm(this.querySelector("[data-fz-savings]")?.textContent) : "",
      subtotal: norm(this.querySelector("[data-fz-subtotal]")?.textContent),
      installment: text("[data-fz-installment-text]"),
      ...(() => {
        const row = /** @type {HTMLElement | null} */ (this.querySelector("[data-fz-offer-row]"));
        const nudge = /** @type {HTMLElement | null} */ (this.querySelector("[data-fz-bundle-nudge]"));
        const shown = Boolean(row && !row.hidden);
        return {
          offerTitle: shown ? text("[data-fz-offer-title]") : "",
          offerAmount: shown ? norm(this.querySelector("[data-fz-offer-amount]")?.textContent) : "",
          nudge: nudge && !nudge.hidden ? "1" : "",
        };
      })(),
      bar: barSignature(
        [...this.querySelectorAll(".fz-goal__marker:not([hidden])")]
          .map((m) => /** @type {HTMLElement} */ (m))
          .sort(
            (a, b) => parseFloat(a.style.getPropertyValue("--fz-at")) - parseFloat(b.style.getPropertyValue("--fz-at")),
          )
          .map((m) => m.dataset.tier),
        parseFloat(
          /** @type {HTMLElement | null} */ (this.querySelector(".fz-goal__fill"))?.style.getPropertyValue(
            "--fz-progress",
          ) ?? "0",
        ),
      ),
    };
    const expected = Object.fromEntries(Object.entries(rules).map(([k, v]) => [k, norm(v)]));
    return { expected, rendered, totals: server, predictedTotals: predicted, state };
  }

  #paint(state, totals, offer) {
    const t = this.#texts(state, totals, offer);
    const set = (sel, fn) => this.querySelectorAll(sel).forEach(fn);

    set("[data-fz-headline]", (el) => {
      if (el.innerHTML !== t.headline) el.innerHTML = t.headline;
    });
    set(".fz-goal", (el) => /** @type {HTMLElement} */ (el.dataset.headline = state.headline));
    // ONE bar: a single fill, and each tier's marker and label moved to its
    // stage (the order can change when exactly one tier is unlocked).
    set(".fz-goal__fill", (el) =>
      /** @type {HTMLElement} */ (el).style.setProperty("--fz-progress", String(state.bar.fill)),
    );
    set(".fz-goal__bar", (el) => {
      const h = /** @type {HTMLElement} */ (el);
      h.dataset.tiers = String(state.bar.order.length);
      h.toggleAttribute("data-done", state.bar.next === null);
    });
    set(".fz-goal__marker, .fz-goal__milestone", (el) => {
      const h = /** @type {HTMLElement} */ (el);
      const marker = state.bar.markers.find((m) => m.tier === h.dataset.tier);
      h.hidden = !marker;
      if (!marker) return;
      h.dataset.unlocked = String(marker.done);
      h.dataset.pos = marker.at === 1 ? "end" : "mid";
      h.style.setProperty("--fz-at", String(marker.at));
      const note = h.querySelector("[data-fz-unlocked-note]");
      if (note) /** @type {HTMLElement} */ (note).hidden = !marker.done;
    });
    set(".fz-offer", (el) => /** @type {HTMLElement} */ (el.dataset.slot = state.slot));
    set("[data-slot-panel]", (el) => {
      /** @type {HTMLElement} */ (el).hidden = /** @type {HTMLElement} */ (el).dataset.slotPanel !== state.slot;
    });
    set("[data-fz-per-pair]", (el) => {
      if (el.innerHTML !== t.perPair) el.innerHTML = t.perPair;
    });
    set(".fz-sum--savings", (el) => /** @type {HTMLElement} */ (el.hidden = !t.savings));
    set("[data-fz-savings]", (el) => (el.textContent = t.savings));
    set("[data-fz-subtotal]", (el) => (el.textContent = t.subtotal));
    set("[data-fz-installment-text]", (el) => {
      if (t.installment && el.innerHTML !== t.installment) el.innerHTML = t.installment;
    });
    // The 2-bundle offer: the nudge at exactly one bundle, the named row once
    // the discount applies. Both exist only where the server rendered them
    // (a cart holding a bundle, in a market that sells them).
    set("[data-fz-bundle-nudge]", (el) => /** @type {HTMLElement} */ (el.hidden = !t.nudge));
    set("[data-fz-offer-row]", (el) => /** @type {HTMLElement} */ (el.hidden = !t.offerAmount));
    set("[data-fz-offer-title]", (el) => {
      if (t.offerTitle && el.textContent !== t.offerTitle) el.textContent = t.offerTitle;
    });
    set("[data-fz-offer-amount]", (el) => {
      if (t.offerAmount) el.textContent = t.offerAmount;
    });
  }

  /**
   * Pop a milestone the first time its tier turns on. Only on a CHANGE: an
   * already-unlocked cart opened on a new page does not celebrate again.
   */
  #noteUnlocks(state, celebrate) {
    const now = { shipping: state.shipping.on && state.shipping.unlocked, bundle: state.bundle.unlocked };
    const before = this.#unlocked;
    this.#unlocked = now;
    if (!celebrate || !before) {
      // A goal already reached when this page opened was reached before it:
      // it is never thrown later, on a dip and a re-crossing (once per cart).
      if (!before && (now.shipping || now.bundle)) {
        for (const tier of /** @type {const} */ (["shipping", "bundle"])) if (now[tier]) this.#celebrated.add(tier);
        writeCelebrated(this.#celebrated);
      }
      return;
    }
    for (const tier of /** @type {const} */ (["shipping", "bundle"])) {
      if (now[tier] && !before[tier]) {
        this.#celebrate(tier);
        this.#confetti(tier);
      }
    }
  }

  /** Once per goal per cart; shown now if the drawer is open, else on its next open. */
  #confetti(tier) {
    if (this.#celebrated.has(tier)) return;
    this.#celebrated.add(tier);
    writeCelebrated(this.#celebrated);
    // Inside the drawer only: the /cart page keeps the marker's pop alone.
    if (!this.#dialog || reducedMotion()) return;
    if (this.#dialog.open) this.#throwConfetti(tier);
    else this.#pendingConfetti = tier;
  }

  #onDrawerOpen = (/** @type {Event} */ event) => {
    if (!(event.target instanceof Node) || !event.target.contains(this)) return;
    const tier = this.#pendingConfetti;
    this.#pendingConfetti = null;
    if (!tier || reducedMotion()) return;
    // After the panel has sprung in, so the pieces leave a marker at rest.
    clearTimeout(this.#confettiTimer);
    this.#confettiTimer = window.setTimeout(() => {
      if (this.#dialog?.open) this.#throwConfetti(tier);
    }, 260);
  };

  #onDrawerClosing = (/** @type {Event} */ event) => {
    if (event.target instanceof Node && event.target.contains(this)) this.#stopConfetti();
  };

  #throwConfetti(tier) {
    const dialog = this.#dialog;
    if (!dialog) return;
    this.#stopConfetti();
    const box = dialog.getBoundingClientRect();
    const marker = this.querySelector(`.fz-goal__marker[data-tier="${tier}"]`)?.getBoundingClientRect();
    // From the goal's own marker, or near the panel's top if it is scrolled away.
    const seen = !!marker && marker.width > 0 && marker.top - box.top >= 0 && marker.top - box.top <= box.height * 0.6;
    const ox = seen ? marker.left + marker.width / 2 - box.left : box.width / 2;
    const oy = seen ? marker.top + marker.height / 2 - box.top : 96;
    const layer = document.createElement("div");
    layer.className = "fz-confetti";
    layer.setAttribute("aria-hidden", "true");
    let left = CONFETTI_PIECES;
    for (let i = 0; i < CONFETTI_PIECES; i++) {
      const piece = document.createElement("span");
      piece.className = "fz-confetti__piece";
      const strip = i % 3 !== 0;
      const w = strip ? 7 + Math.random() * 3 : 6 + Math.random() * 2;
      const h = strip ? 3 + Math.random() * 2 : w;
      piece.style.cssText = `left:${ox - w / 2}px;top:${oy - h / 2}px;width:${w}px;height:${h}px;background:${
        CONFETTI_COLOURS[i % CONFETTI_COLOURS.length]
      };${strip ? "" : "border-radius:50%;"}`;
      layer.append(piece);
      // Up and out in a wide fan, then down into the drawer. The marker sits
      // near the top of the panel, so the rise is capped short of the panel's
      // edge (the layer clips) and the spread goes sideways instead.
      const angle = ((-90 + (Math.random() * 2 - 1) * 70) * Math.PI) / 180;
      const speed = 140 + Math.random() * 150;
      const dx = Math.cos(angle) * speed * 1.2;
      const rise = Math.max(Math.sin(angle) * speed, -(oy - 12));
      const fall = 220 + Math.random() * 220;
      const spin = (Math.random() * 2 - 1) * 540;
      const animation = piece.animate(
        [
          { transform: "translate(0, 0) rotate(0deg) scale(0.6)", opacity: 0, offset: 0, easing: CONFETTI_RISE },
          { opacity: 1, offset: 0.06 },
          {
            transform: `translate(${dx * 0.6}px, ${rise}px) rotate(${spin * 0.4}deg) scale(1)`,
            offset: 0.3,
            easing: CONFETTI_FALL,
          },
          { opacity: 1, offset: 0.78 },
          { transform: `translate(${dx}px, ${fall}px) rotate(${spin}deg) scale(1)`, opacity: 0, offset: 1 },
        ],
        { duration: 1300 + Math.random() * 500, delay: Math.random() * 120, fill: "both" },
      );
      animation.onfinish = () => {
        piece.remove();
        if (--left === 0 && this.#confettiLayer === layer) this.#stopConfetti();
      };
    }
    dialog.append(layer);
    this.#confettiLayer = layer;
  }

  /** Interruptible: whatever is in the air goes at once. */
  #stopConfetti() {
    clearTimeout(this.#confettiTimer);
    this.#confettiLayer?.remove();
    this.#confettiLayer = null;
  }

  #celebrate(tier) {
    const el = this.querySelector(`.fz-goal__marker[data-tier="${tier}"]`);
    if (!el) return;
    el.classList.remove("is-celebrating");
    // Restart the animation on the next frame rather than forcing a reflow.
    requestAnimationFrame(() => {
      el.classList.add("is-celebrating");
      clearTimeout(this.#celebrateTimer);
      this.#celebrateTimer = window.setTimeout(() => el.classList.remove("is-celebrating"), 900);
    });
  }

  // ---- Events -------------------------------------------------------------

  /** A stepper moved: Horizon sends the change 300 ms later; the bar moves now. */
  #onQuantity = (/** @type {Event} */ event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;
    // The reason and the names move with the tap, before the server answers.
    this.#syncSteppers();
    const detail = /** @type {any} */ (event).detail ?? {};
    const line = this.#lines[(detail.cartLine ?? 0) - 1];
    if (!line || !this.#tiers) return;
    this.#lines = this.#tiers.withQuantity(this.#lines, line.key, detail.quantity);
    this.#predict();
  };

  /** Removes and updates dispatched by <cart-items-component> around us, and failures. */
  #onLinesUpdate = (/** @type {any} */ event) => {
    // Every cart action, ours or the page's, starts with no stale refusal.
    this.#clearLineErrors();
    const host = this.closest("cart-items-component");
    const ours = event.target === host || (event.target instanceof Node && this.contains(event.target));
    if (!ours) {
      if (event.action === "add") {
        // A form sends the bare id; the Storefront API sends a gid.
        const id = String(event.lines?.[0]?.merchandiseId ?? "");
        const add = { variantId: id.slice(id.lastIndexOf("/") + 1) };
        this.#pageAdd = add;
        const painted = event.fzOptimistic ? this.#paintOptimistic(event.fzOptimistic) : false;
        // The refusal, if any, arrives before the promise settles (product-form
        // dispatches it first), so settling ends the window for it. A pair
        // painted ahead of Shopify that its answer did not replace (a refused
        // or failed add) goes too.
        const done = () => {
          if (this.#pageAdd === add) this.#pageAdd = null;
          if (painted) this.#clearOptimistic();
        };
        event.promise?.then(done, done);
      }
      return;
    }
    if (!this.#tiers) return;
    if (event.action === "remove" || event.action === "update") {
      for (const l of event.lines ?? []) {
        if (l && l.id && typeof l.quantity === "number") {
          this.#lines = this.#tiers.withQuantity(this.#lines, l.id, l.quantity);
        }
      }
      if (event.action === "remove") this.#predict();
    }
    event.promise
      ?.then((/** @type {any} */ result) => {
        if (result?.detail?.didError) this.#fail();
      })
      .catch((/** @type {any} */ error) => {
        if (error?.name !== "AbortError") this.#fail();
      });
  };

  /**
   * THE PAIR AT THE TAP (Luke, 10 Oct: a grid card's add opened the drawer
   * "immediately but it is blank", and the pair arrived 3-5 s later). Horizon
   * opens this drawer in the add's own dispatch, before Shopify has answered,
   * and an empty cart then said "No Foozies in here yet" for the whole round
   * trip. A grid card puts what it shows on the event (assets/fz-collection.js,
   * `fzOptimistic`), and the pair is painted here at once as a line of the same
   * shape and size: it grows in as I-56's rows do, and the server's line takes
   * its place when the add's answer is morphed in. Its ids are ones the server
   * never sends, so Horizon's morph (which matches by id) inserts the real line
   * rather than dragging this one. Only in the drawer; only a pair the cart does
   * not already hold (a second of the same pair changes a quantity, not a line).
   * The stepper and the bin are stand-ins, disabled and hidden from assistive
   * tech; the cart is aria-busy until the answer lands.
   * @param {{ variantId: string, quantity: number, title: string, variant: string, image: string, now: string, compare: string }} o
   * @returns {boolean} whether a line was painted
   */
  #paintOptimistic(o) {
    if (this.dataset.context !== "drawer" || !o || !o.variantId || !o.title) return false;
    if (this.querySelector(`.fz-line[data-variant-id="${CSS.escape(o.variantId)}"]`)) return false;
    this.#clearOptimistic();
    const minus =
      '<path d="M2.75 7H11.25" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"/>';
    const plus = `${minus}<path d="M7 2.75L7 11.25" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"/>`;
    const icon = (paths) =>
      `<span class="svg-wrapper icon-plus"><svg aria-hidden="true" width="14" height="14" viewBox="0 0 14 14" fill="none">${paths}</svg></span>`;
    const row = document.createElement("div");
    row.id = `fz-optimistic-line-${o.variantId}`;
    row.className = "fz-line fz-line--optimistic";
    row.dataset.fzOptimistic = "";
    row.dataset.variantId = o.variantId;
    row.innerHTML = `
      <span class="fz-line__media" aria-hidden="true">${
        o.image
          ? `<img class="fz-line__image" src="${escapeHtml(o.image)}" alt="" width="80" height="80" decoding="async">`
          : ""
      }</span>
      <div class="fz-line__info">
        <div class="fz-line__top"><span class="fz-line__title">${escapeHtml(o.title)}</span>
          <span class="fz-line__remove" aria-hidden="true"><svg viewBox="0 0 24 24" width="18" height="18"></svg></span></div>
        ${o.variant ? `<p class="fz-line__variant">${escapeHtml(o.variant)}</p>` : ""}
        <div class="fz-line__bottom">
          <p class="fz-line__price">${o.compare ? `<s class="fz-line__compare">${escapeHtml(o.compare)}</s>` : ""}${
            o.now ? `<span class="fz-line__now">${escapeHtml(o.now)}</span>` : ""
          }</p>
          <div class="quantity-selector-wrapper" aria-hidden="true"><div class="quantity-selector fz-line__stepper">
            <button class="button quantity-minus button-unstyled" type="button" disabled tabindex="-1">${icon(minus)}</button>
            <input type="number" value="${Number(o.quantity) || 1}" disabled tabindex="-1">
            <button class="button quantity-plus button-unstyled" type="button" disabled tabindex="-1">${icon(plus)}</button>
          </div></div>
        </div>
      </div>`;
    let list = this.querySelector(".fz-lines");
    if (!list) {
      // An empty cart: the pair in place of "No Foozies in here yet".
      const empty = /** @type {HTMLElement | null} */ (this.querySelector(".fz-cart__empty"));
      if (empty && !empty.hidden) {
        empty.hidden = true;
        empty.dataset.fzOptimisticHid = "";
      }
      const main = document.createElement("div");
      main.id = "fz-optimistic-main";
      main.className = "fz-cart__main";
      main.dataset.fzOptimistic = "";
      list = document.createElement("div");
      list.className = "fz-lines";
      main.append(list);
      const header = this.querySelector(".fz-cart__header");
      if (header) header.after(main);
      else this.prepend(main);
    }
    list.prepend(row);
    if (!reducedMotion()) row.classList.add("adding");
    this.setAttribute("aria-busy", "true");
    return true;
  }

  /** Whatever the server's answer did not replace goes; the empty state comes back. */
  #clearOptimistic() {
    for (const node of this.querySelectorAll("[data-fz-optimistic]")) node.remove();
    for (const node of this.querySelectorAll("[data-fz-optimistic-hid]")) {
      /** @type {HTMLElement} */ (node).hidden = false;
      node.removeAttribute("data-fz-optimistic-hid");
    }
    this.removeAttribute("aria-busy");
  }

  /**
   * A line's refusal ("Only 3 items were added to your cart due to
   * availability.") belongs to the tap that caused it. Horizon writes it
   * after a refused change and never takes it down, so the UX run of
   * 2026-09-26 still showed a stepper's refusal in the drawer two actions
   * later, beside the PDP's message about a different add. The next cart
   * action clears it. A refusal of THAT action is written after its
   * response, so it is never the one cleared.
   */
  #clearLineErrors() {
    for (const box of this.querySelectorAll(".fz-line__error:not(.hidden)")) {
      box.classList.add("hidden");
      const text = box.querySelector(".fz-line__error-text");
      if (text) text.textContent = "";
    }
    this.#refusal = null;
    document.documentElement.removeAttribute("data-fz-cart-refusal");
  }

  /**
   * ONE REFUSAL, ON THE SURFACE THE SHOPPER IS LOOKING AT (UX run 2026-09-26,
   * decided with A3). An add from the page opens this drawer before Shopify
   * answers (Horizon's cart-drawer), and the drawer is modal at every width
   * (I-92). So when Shopify refuses the add, the PDP's message sits under the
   * drawer's dim, inert to a screen reader, and live's MonsterCart says
   * nothing at all. While the drawer is open it says Shopify's words on that
   * pair's line, and sets `data-fz-cart-refusal`, on which fz-cart.css hides
   * the PDP's copy. Closed, the drawer says nothing and the PDP keeps it.
   */
  #onCartError = (/** @type {any} */ event) => {
    const add = this.#pageAdd;
    if (!add || !this.#dialog?.open) return;
    const host = this.closest("cart-items-component");
    if (event.target === host || (event.target instanceof Node && this.contains(event.target))) return;
    if (event.code !== "INVALID" || !event.error) return;
    this.#pageAdd = null;
    // Shopify's 422 can say "Cart Error" and keep the sentence that explains
    // it in `description`, which product-form passes on as detail.description.
    const description = typeof event.detail?.description === "string" ? event.detail.description.trim() : "";
    this.#refusal = { variantId: add.variantId, message: description || String(event.error) };
    this.#paintRefusal();
  };

  /**
   * The refusal on its line; with no line for that pair, the PDP keeps its
   * copy. Idempotent: it runs again after every re-render (see
   * connectedCallback), and the flag that hides the PDP's copy is only ever
   * set while the line really shows the words.
   */
  #paintRefusal() {
    const refusal = this.#refusal;
    if (!refusal || !this.#dialog?.open) return;
    const row = /** @type {HTMLElement[]} */ ([...this.querySelectorAll(".fz-line[data-variant-id]")]).find(
      (el) => el.dataset.variantId === refusal.variantId,
    );
    const box = row?.querySelector(".fz-line__error");
    const text = box?.querySelector(".fz-line__error-text");
    if (!box || !text) {
      document.documentElement.removeAttribute("data-fz-cart-refusal");
      return;
    }
    if (text.textContent !== refusal.message) text.textContent = refusal.message;
    if (box.classList.contains("hidden")) box.classList.remove("hidden");
    document.documentElement.setAttribute("data-fz-cart-refusal", "");
  }

  /**
   * Closed, the drawer no longer covers the PDP, so the PDP's copy shows again,
   * and the refusal is not painted again on a later open.
   */
  #onDrawerClose = () => {
    this.#refusal = null;
    document.documentElement.removeAttribute("data-fz-cart-refusal");
  };

  #fail() {
    this.#revert();
    const err = /** @type {HTMLElement | null} */ (this.querySelector("[data-fz-error]"));
    if (err) {
      err.hidden = false;
      window.setTimeout(() => (err.hidden = true), 5000);
    }
  }

  #onClick = (/** @type {MouseEvent} */ event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const add = /** @type {HTMLButtonElement | null} */ (target.closest("[data-fz-add]"));
    if (add) {
      event.preventDefault();
      if (add.dataset.fzAdd === "next") this.#addNext(add);
      else this.#addUpsell(add);
      return;
    }
    if (target.closest("[data-fz-details-open]")) {
      event.preventDefault();
      this.#details(true);
      return;
    }
    if (target.closest("[data-fz-details-close]")) {
      event.preventDefault();
      this.#details(false);
    }
  };

  #onChange = (/** @type {Event} */ event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.matches("[data-fz-covered]")) {
      this.#toggleCovered(/** @type {HTMLInputElement} */ (target));
    } else if (target.matches("select[data-fz-upsell-select]")) {
      this.#syncUpsell(/** @type {HTMLSelectElement} */ (target));
    }
  };

  #onKeyDown = (/** @type {KeyboardEvent} */ event) => {
    // Esc closes the detail panel first, and only the panel: <theme-drawer>
    // leaves an Escape it finds already handled alone.
    if (event.key === "Escape" && this.hasAttribute("data-details-open")) {
      event.preventDefault();
      this.#details(false);
    }
  };

  // ---- The drawer's own adds ---------------------------------------------

  #addNext(button) {
    const next = this.#server?.next;
    if (!next) return;
    this.#lines = this.#tiers.withAdded(this.#lines, next);
    this.#predict();
    this.#request({ add: Number(button.dataset.variantId), button });
  }

  #addUpsell(button) {
    const scope = button.closest("[data-fz-upsell], [data-fz-details]") ?? this;
    const select = /** @type {HTMLSelectElement | HTMLInputElement | null} */ (
      scope.querySelector("[data-fz-upsell-select]")
    );
    if (!select) return;
    const option = select instanceof HTMLSelectElement ? select.selectedOptions[0] : select;
    const variantId = Number(select.value);
    if (!variantId || !option) return;
    const upsell = /** @type {HTMLElement | null} */ (this.querySelector("[data-fz-upsell]"));
    if (this.#server) {
      const unitCents = Number(option.dataset.unitCents || 0);
      this.#lines = this.#tiers.withAdded(this.#lines, {
        variantId,
        unitCents,
        compareCents: Number(option.dataset.compareCents || unitCents),
        pair: upsell?.dataset.pair === "true",
        ships: true,
      });
      this.#predict();
    }
    if (this.hasAttribute("data-details-open")) this.#details(false);
    this.#request({ add: variantId, button });
  }

  #toggleCovered(input) {
    const covered = this.#server?.covered;
    if (!covered) return;
    if (input.checked) {
      this.#lines = this.#tiers.withAdded(this.#lines, covered);
      this.#predict();
      this.#request({ add: covered.variantId, button: input });
    } else if (covered.key) {
      this.#lines = this.#tiers.withQuantity(this.#lines, covered.key, 0);
      this.#predict();
      this.#request({ remove: covered.key, button: input });
    }
  }

  /**
   * One add or one remove, the way Horizon's product form does it: the event
   * first (the drawer, the header and the pixels listen), then the request,
   * then the event's promise resolved with the re-rendered sections.
   * @param {{ add?: number, remove?: string, button: HTMLElement }} op
   */
  async #request({ add, remove, button }) {
    const deferred = CartLinesUpdateEvent.createPromise();
    const ids = sectionIds();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: add ? "add" : "remove",
        context: "fz-drawer",
        lines: add ? [{ merchandiseId: String(add), quantity: 1 }] : [{ id: remove, quantity: 0 }],
        promise: deferred.promise,
      }),
    );
    button.setAttribute("aria-busy", "true");
    try {
      const url = add ? Theme.routes.cart_add_url : Theme.routes.cart_change_url;
      const body = add
        ? { items: [{ id: add, quantity: 1 }], sections: ids.join(","), sections_url: window.location.pathname }
        : { id: remove, quantity: 0, sections: ids.join(","), sections_url: window.location.pathname };
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        this.dispatchEvent(
          new CartErrorEvent({ error: data.description || data.message || "Cart update failed", code: "INVALID" }),
        );
        const cart = await (
          await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: "application/json" } })
        ).json();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, itemCount: 0, source: "fz-cart", didError: true },
        });
        return;
      }
      const cart = add
        ? await (await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: "application/json" } })).json()
        : data;
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, itemCount: 1, source: "fz-cart", sections: data.sections, didError: false },
      });
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(new CartErrorEvent({ error: String(error), code: "SERVICE_UNAVAILABLE" }));
    } finally {
      button.removeAttribute("aria-busy");
    }
  }

  // ---- The upsell ---------------------------------------------------------

  /** Keep the card and the panel on the same variant, price and picture. */
  #syncUpsell(select) {
    const option = select.selectedOptions[0];
    if (!option) return;
    for (const other of this.querySelectorAll("select[data-fz-upsell-select]")) {
      if (other !== select) /** @type {HTMLSelectElement} */ (other).value = select.value;
    }
    const price = option.dataset.price;
    if (price) {
      this.querySelectorAll(".fz-upsell__price, .fz-details__price").forEach((el) => {
        const template = /** @type {HTMLElement} */ (el).dataset.t;
        if (template) el.innerHTML = this.#tiers.fill(template, { price: escapeHtml(price) });
      });
    }
    const image = option.dataset.image;
    if (image) {
      this.querySelectorAll(".fz-upsell__image, .fz-details__image").forEach((img) => {
        /** @type {HTMLImageElement} */ (img).srcset = "";
        /** @type {HTMLImageElement} */ (img).src = image;
      });
    }
  }

  /** The slide-in detail panel: focus in on open, back to its trigger on close. */
  #details(open) {
    const panel = /** @type {HTMLElement | null} */ (this.querySelector("[data-fz-details]"));
    if (!panel) return;
    const trigger = /** @type {HTMLElement | null} */ (this.querySelector("[data-fz-details-open]"));
    const back = /** @type {HTMLElement | null} */ (this.querySelector(".fz-cart__back"));
    this.toggleAttribute("data-details-open", open);
    panel.inert = !open;
    trigger?.setAttribute("aria-expanded", String(open));
    if (back) back.hidden = !open;
    if (open) back?.focus({ preventScroll: true });
    else trigger?.focus({ preventScroll: true });
  }
}

if (!customElements.get("fz-cart")) customElements.define("fz-cart", FzCart);

/**
 * The upsell's fallback: Shopify's complementary recommendation for the
 * shopper's newest line, asked for once, when no drawer_upsell entry applies.
 */
class FzCartRecs extends HTMLElement {
  connectedCallback() {
    const url = this.dataset.url;
    if (!url || this.dataset.loaded) return;
    this.dataset.loaded = "true";
    fetch(url)
      .then((r) => (r.ok ? r.text() : ""))
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, "text/html");
        const card = doc.querySelector("[data-fz-upsell]");
        if (card) this.replaceChildren(card);
      })
      .catch(() => {});
  }
}
if (!customElements.get("fz-cart-recs")) customElements.define("fz-cart-recs", FzCartRecs);

/**
 * The express checkout row wakes two frames after the drawer opens and sleeps
 * when it closes (see .fz-cart__express in assets/fz-cart.css for why). The
 * flag lives on <theme-drawer>, outside everything a cart re-render morphs.
 */
document.addEventListener("theme-drawer:open", (event) => {
  const drawer = /** @type {HTMLElement} */ (event.target);
  if (drawer.id !== "cart-drawer") return;
  drawer.removeAttribute("data-express-settled");
  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      drawer.setAttribute("data-express-live", "");
      holdExpress(drawer);
    }),
  );
});
document.addEventListener("theme-drawer:close", (event) => {
  const drawer = /** @type {HTMLElement} */ (event.target);
  if (drawer.id !== "cart-drawer") return;
  drawer.removeAttribute("data-express-live");
  drawer.removeAttribute("data-express-settled");
});

/*
 * THE WALLETS' LOADING STATE IS TALLER THAN THEIR BUTTONS (A7 perf, 9 Oct).
 * Shopify's <shopify-accelerated-checkout-cart> draws a skeleton per wallet it
 * might offer, stacked, and only then settles to the buttons it can offer, in
 * one row when they fit. The footer is pinned to the drawer's bottom, so the
 * collapse moved the whole footer: 0.2037 of the gate's 0.2206 CLS was that
 * one shift, 660 ms after the drawer opened (the W2 attribution run), past the
 * 500 ms that input excuses, so a shopper's page records it too. So the row is
 * held at its reserved height, clipped (assets/fz-cart.css), until its buttons
 * are in and their size has stopped changing; the height it settles at is the
 * reservation from then on (--fz-express-h on <theme-drawer>, which no cart
 * re-render touches, and in sessionStorage for the next page). A row that
 * settles TALLER than the reservation (a phone too narrow for one row) is
 * released only after it has held that height a while longer, so a skeleton
 * that still has to collapse is never mistaken for a settled stack.
 */
const EXPRESS_HEIGHT = "fz-express-h";
const EXPRESS_STABLE_MS = 300;
const EXPRESS_TALL_MS = 1500;
const EXPRESS_GIVE_UP_MS = 8000;
{
  let saved = null;
  try {
    saved = sessionStorage.getItem(EXPRESS_HEIGHT);
  } catch {
    /* storage refused: Shopify's one-row height (fz-cart.css) */
  }
  const drawer = document.getElementById("cart-drawer");
  if (drawer && saved !== null && Number.isFinite(Number(saved))) {
    drawer.style.setProperty("--fz-express-h", `${Number(saved)}px`);
  }
}

/** @param {HTMLElement} drawer */
function holdExpress(drawer) {
  const row = /** @type {HTMLElement | null} */ (drawer.querySelector(".fz-cart__express"));
  const wallets = row && row.querySelector("shopify-accelerated-checkout-cart");
  if (!row || !wallets) {
    drawer.setAttribute("data-express-settled", "");
    return;
  }
  const content = /** @type {HTMLElement} */ (row.firstElementChild || wallets);
  const reserved = () => row.getBoundingClientRect().height;
  let timer = 0;
  let tallSince = 0;
  const finish = () => {
    sizes.disconnect();
    children.disconnect();
    window.clearTimeout(timer);
    window.clearTimeout(giveUp);
  };
  const release = () => {
    finish();
    if (!drawer.hasAttribute("data-express-live")) return; // closed meanwhile: held again on the next open
    const height = Math.round(content.getBoundingClientRect().height);
    drawer.style.setProperty("--fz-express-h", `${height}px`);
    try {
      sessionStorage.setItem(EXPRESS_HEIGHT, String(height));
    } catch {
      /* this page keeps it on the drawer */
    }
    drawer.setAttribute("data-express-settled", "");
  };
  const check = () => {
    window.clearTimeout(timer);
    if (!wallets.querySelector('[slot^="button-"]')) return; // still the skeleton
    timer = window.setTimeout(() => {
      if (content.getBoundingClientRect().height > reserved() + 1) {
        if (!tallSince) tallSince = performance.now();
        if (performance.now() - tallSince < EXPRESS_TALL_MS) {
          timer = window.setTimeout(check, EXPRESS_TALL_MS);
          return;
        }
      }
      release();
    }, EXPRESS_STABLE_MS);
  };
  const sizes = new ResizeObserver(() => {
    tallSince = 0;
    check();
  });
  const children = new MutationObserver(check);
  sizes.observe(content);
  children.observe(wallets, { childList: true });
  const giveUp = window.setTimeout(release, EXPRESS_GIVE_UP_MS);
}

/**
 * THE DRAWER FROM THE ADD'S OWN ANSWER (pdp-add-speed, 10 Oct). Horizon's
 * product form asks /cart/add.js for the drawer, rendered (`sections`), and
 * then fetches /cart.json before it settles the add's event, and Horizon morphs
 * the drawer only when it settles: two round trips before the pair the shopper
 * added is in it. One allowlisted line in assets/product-form.js hands this
 * file the add's sections as soon as they arrive, and they are morphed now, as
 * <cart-items-component> would morph them; its own morph of the same HTML on
 * settle then finds nothing to change. (A grid card does the same in
 * assets/fz-collection.js.) A failure leaves it to Horizon's morph.
 */
document.addEventListener("fz:cart-sections", async (/** @type {any} */ event) => {
  const sections = event.detail;
  if (!sections || typeof sections !== "object") return;
  try {
    const { morphSection } = await import("@theme/section-renderer");
    for (const el of document.querySelectorAll("cart-items-component[data-section-id]")) {
      const sectionId = /** @type {HTMLElement} */ (el).dataset.sectionId;
      const html = sectionId && sections[sectionId];
      if (html) await morphSection(sectionId, html, { mode: el.hasAttribute("data-drawer") ? "hydration" : "full" });
    }
  } catch {
    /* Horizon's own morph, when the event settles, still lands it */
  }
});

/**
 * ANDROID BACK, AND ANY OTHER NATIVE CLOSE (in-app review IA-02). On a phone
 * the drawer is a modal <dialog>, and the platform can close it by itself:
 * Android's back gesture fires `cancel` and then closes the dialog, with no
 * keydown for <theme-drawer> to see. The drawer would then keep its [open]
 * state and its scroll lock, and the page would stay frozen under nothing. So
 * a `cancel` goes through the drawer's own animated close, and a `close` that
 * arrives while the drawer still thinks it is open finishes the job.
 */
{
  const drawer = /** @type {any} */ (document.getElementById("cart-drawer"));
  const dialog = drawer?.querySelector("dialog");
  dialog?.addEventListener("cancel", (event) => {
    if (!drawer.hasAttribute("open")) return;
    event.preventDefault();
    drawer.close?.();
  });
  // `close` is queued, so by the time it arrives Horizon may have re-opened the
  // dialog on purpose (crossing its 990 px breakpoint, or bringing the drawer
  // to the front): only a dialog that is STILL closed means the platform did it.
  dialog?.addEventListener("close", () => {
    if (drawer.hasAttribute("open") && !dialog.open) drawer.close?.();
  });
}

/**
 * THE BUNDLE PAGE DOES NOT AUTO-OPEN THE DRAWER. Session A5a's "{n} of 4
 * added" counter is that page's feedback, and four adds would otherwise mean
 * four drawer openings. Horizon decides to open synchronously, while the event
 * is dispatched, so the attribute is lifted for exactly that dispatch. The
 * counter itself opens the drawer when the 4th pair goes in (Luke, L-101;
 * assets/fz-collection.js).
 */
window.addEventListener(
  StandardEvents.cartLinesUpdate,
  (/** @type {any} */ event) => {
    const context = event.context ?? event.detail?.context;
    const exempt =
      context === "fz-bundle" || (event.target instanceof Element && event.target.closest("[data-fz-no-auto-open]"));
    if (!exempt) return;
    const drawer = document.querySelector("cart-drawer-component[auto-open]");
    if (!drawer) return;
    drawer.removeAttribute("auto-open");
    queueMicrotask(() => drawer.setAttribute("auto-open", ""));
  },
  { capture: true },
);

/**
 * - AT 1 REMOVES THE LINE (cart parity run, 2026-10-08; live's MonsterCart
 * does the same). The line's stepper goes down to 0 (fz-cart-line passes
 * min: 0), so - stays pressable at 1, and #syncSteppers marks it. The press
 * goes to the host's own onLineItemRemove, which is exactly what the line's
 * bin calls: the same request, the same I-56 row-out, no 300 ms debounce and
 * no "0" in the box while it waits. Horizon's declarative on:click listens on
 * the DOCUMENT while capturing, so this listens on the window, which the
 * capture phase reaches first, and stops the press there. Without this file
 * the stepper still works: Horizon turns its 0 into the same remove.
 */
window.addEventListener(
  "click",
  (event) => {
    const minus =
      event.target instanceof Element
        ? event.target.closest("fz-cart .fz-line .quantity-minus[data-fz-removes]")
        : null;
    if (!(minus instanceof HTMLButtonElement) || minus.disabled) return;
    const row = /** @type {HTMLElement} */ (minus.closest(".fz-line"));
    const input = /** @type {HTMLInputElement | null} */ (row.querySelector("input[data-cart-line]"));
    const host = /** @type {any} */ (row.closest("cart-items-component"));
    const line = Number(input?.dataset.cartLine || row.dataset.line);
    if (!input || Number(input.value) > 1 || !line || typeof host?.onLineItemRemove !== "function") return;
    event.preventDefault();
    event.stopPropagation();
    host.onLineItemRemove(line);
  },
  { capture: true },
);

/**
 * THE TAB NUDGE (plan section 3.4): with something in the cart, the tab reads
 * "Your cart will expire soon!" while the shopper is away, and the favicon
 * becomes an alarm clock. That is what the store's Tab Recovery app does today,
 * as a ScriptTag Shopify injects into EVERY theme, this one included, so while
 * the app is installed it is the app that nudges, identically in both arms of
 * the test, and this stays asleep. It wakes if the app is removed. The wording
 * is Luke's call (control-room item L-05); the theme setting
 * `fz_cart_tab_nudge` switches it off.
 */
function tabNudge() {
  const config = /** @type {HTMLElement | null} */ (document.querySelector("[data-fz-tab-nudge]"));
  const title = config?.dataset.title;
  if (!title) return;
  let saved = /** @type {{ title: string, icons: Array<[HTMLLinkElement, string]> } | null} */ (null);
  const clock =
    "data:image/svg+xml," +
    encodeURIComponent(
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><text y=".9em" font-size="90">⏰</text></svg>',
    );
  document.addEventListener("visibilitychange", () => {
    if (document.querySelector('script[src*="trecovery"]')) return;
    const count = Number(/** @type {HTMLElement | null} */ (document.querySelector("fz-cart"))?.dataset.itemCount || 0);
    if (document.hidden && count > 0 && !saved) {
      const icons = /** @type {Array<[HTMLLinkElement, string]>} */ (
        [...document.querySelectorAll('link[rel~="icon"]')].map((l) => [
          /** @type {HTMLLinkElement} */ (l),
          /** @type {HTMLLinkElement} */ (l).href,
        ])
      );
      saved = { title: document.title, icons };
      document.title = title;
      for (const [link] of icons) link.href = clock;
    } else if (!document.hidden && saved) {
      document.title = saved.title;
      for (const [link, href] of saved.icons) link.href = href;
      saved = null;
    }
  });
}
tabNudge();
